import {timeUtil} from '@/helper/time';
import {eq, minus, numUtil} from '@/helper/num';
import {spotOrderUtil} from '@/helper/order-spot';
import {convertPrice} from './util';
import type {
  ws,
  OrderData,
  AssetDigital,
  SpotTradeMode,
  SpotActiveOrder,
  MarginActiveOrder,
  SpotConditionalOrder,
  MarginConditionalOrder,
  SpotFillOrder,
  MarginFillOrder,
  SpotHistoryOrder,
  MarginHistoryOrder,
  SpotPair,
  MarginPair,
} from '@/types';

type SpotOpenOrderRaw = ws.OpenSpotOrderRaw | ws.ClosedSpotOrderRaw;
type MarginClosedOrderRaw = ws.OpenMarginOrderRaw | ws.ClosedMarginOrderRaw;
type OpenOrderRaw = MarginClosedOrderRaw | SpotOpenOrderRaw;
type BaseOrderRaw = ws.SpotOrderRaw | ws.MarginOrderRaw;
type FillOrderRaw = ws.FillSpotOrderRaw | ws.FillMarginOrderRaw;

export interface ActiveOrder extends Omit<SpotActiveOrder, 'priceEp'>, Omit<MarginActiveOrder, 'priceEp'> {
  priceEp: AssetDigital;
  displayQty?: number;
  icebergBaseQty?: string;
  icebergQuoteQty?: string;
}

export interface ConditionalOrder
  extends Omit<SpotConditionalOrder, 'stopPxEp'>,
    Omit<MarginConditionalOrder, 'stopPxEp'> {
  stopPxEp: AssetDigital;
  displayQty?: number;
  icebergBaseQty?: string;
  icebergQuoteQty?: string;
}

export type FillOrder = SpotFillOrder | MarginFillOrder;
export type HistoryOrder = SpotHistoryOrder | MarginHistoryOrder;
export type TradePair = SpotPair | MarginPair;

const IcebergOnlineTime = 1721887200000;

export function convertActiveOrderData<OrderRaw extends OpenOrderRaw>(
  OrderRaws: OrderRaw[],
  tradePairs: TradePair[],
  tradePair: TradePair,
  displayAllSymbols: boolean
): OrderData<ActiveOrder> {
  const count = OrderRaws.length;
  const orders = displayAllSymbols
    ? OrderRaws
    : OrderRaws.filter((orderRaw: OrderRaw) => orderRaw.symbol === tradePair.symbol);
  const displayOrders = orders
    .map((orderRaw: OrderRaw) => convertToActiveOrder(orderRaw, tradePairs, tradePair.mode))
    .filter(Boolean)
    .sort((a: ActiveOrder, b: ActiveOrder) => b.createTimestamp - a.createTimestamp);
  return {
    orders: displayOrders,
    count,
  };
}

export function convertConditionalOrderData<OrderRaw extends OpenOrderRaw>(
  OrderRaws: OrderRaw[],
  tradePairs: TradePair[],
  tradePair: TradePair,
  displayAllSymbols: boolean
): OrderData<ConditionalOrder> {
  const count = OrderRaws.length;
  const orders = displayAllSymbols
    ? OrderRaws
    : OrderRaws.filter((orderRaw: OrderRaw) => orderRaw.symbol === tradePair.symbol);
  const displayOrders = orders
    .map((orderRaw: OrderRaw) => convertToConditionalOrder(orderRaw, tradePairs, tradePair.mode))
    .filter(Boolean)
    .sort((a: ConditionalOrder, b: ConditionalOrder) => b.createTimestamp - a.createTimestamp);
  return {
    orders: displayOrders,
    count,
  };
}

export function convertFillOrderData<OrderRaw extends FillOrderRaw>(
  OrderRaws: OrderRaw[],
  tradePairs: TradePair[],
  tradePair: TradePair,
  displayAllSymbols: boolean
): OrderData<FillOrder> {
  const orders = displayAllSymbols
    ? OrderRaws
    : OrderRaws.filter((orderRaw: OrderRaw) => orderRaw.symbol === tradePair.symbol);
  const displayOrders = orders
    .map((orderRaw: OrderRaw) => convertToFillOrder(orderRaw, tradePairs, tradePair.mode))
    .filter(Boolean)
    .sort((a: FillOrder, b: FillOrder) => b.transactTimestamp - a.transactTimestamp);
  return {orders: displayOrders};
}

export function convertHistoryOrderData<OrderRaw extends BaseOrderRaw>(
  OrderRaws: OrderRaw[],
  tradePairs: TradePair[],
  tradePair: TradePair,
  displayAllSymbols: boolean
): OrderData<HistoryOrder> {
  const orders = displayAllSymbols
    ? OrderRaws
    : OrderRaws.filter((orderRaw: OrderRaw) => orderRaw.symbol === tradePair.symbol);
  const displayOrders = orders
    .map((orderRaw: OrderRaw) => convertToHistoryOrder<OrderRaw>(orderRaw, tradePairs, tradePair.mode))
    .filter(Boolean)
    .sort((a: HistoryOrder, b: HistoryOrder) => b.createTimestamp - a.createTimestamp);
  return {orders: displayOrders};
}

function convertToActiveOrder<OrderRaw extends OpenOrderRaw>(
  raw: OrderRaw,
  tradePairs: TradePair[],
  mode: SpotTradeMode = 'Spot'
): ActiveOrder {
  const {
    orderID,
    symbol,
    side,
    qtyType,
    baseQtyEv,
    quoteQtyEv,
    leavesBaseQtyEv,
    leavesQuoteQtyEv,
    ordType,
    ordStatus,
    priceEp,
    createTimeNs,
    displayQty,
  } = raw;
  const tradePair = tradePairs.find((o) => o.symbol === symbol);
  if (!tradePair) {
    return null;
  }
  const {
    displaySymbol,
    pricePrecision,
    baseCurrency,
    displayBaseCurrency,
    quoteCurrency,
    baseQtyPrecision,
    quoteQtyPrecision,
  } = tradePair;
  const {baseQtyScale, quoteQtyScale} = getQtyScale<TradePair>(mode, tradePair);

  const baseQty = numUtil.floor(baseQtyEv, baseQtyPrecision, baseQtyScale);
  const baseRemainingQty = numUtil.floor(leavesBaseQtyEv, baseQtyPrecision, baseQtyScale);
  const baseFilledQty = numUtil.floor(minus(baseQtyEv, leavesBaseQtyEv), baseQtyPrecision, baseQtyScale);
  const quoteQty = numUtil.floor(quoteQtyEv, quoteQtyPrecision, quoteQtyScale);
  const quoteRemainingQty = numUtil.floor(leavesQuoteQtyEv, quoteQtyPrecision, quoteQtyScale);
  const quoteFilledQty = numUtil.floor(minus(quoteQtyEv, leavesQuoteQtyEv), quoteQtyPrecision, quoteQtyScale);

  const qty = spotOrderUtil.calcQty(qtyType, baseQty, quoteQty);
  const qtyEv = Number(qtyType === 'ByBase' ? baseQtyEv : quoteQtyEv);
  const qtyCurrency = spotOrderUtil.getByQtyType(qtyType, displayBaseCurrency, quoteCurrency);
  const value = spotOrderUtil.calcValue({
    qtyType,
    priceEp,
    baseQtyEv,
    quoteQtyEv,
    baseQtyScale,
    quoteQtyScale,
    quoteQtyPrecision,
    baseQtyPrecision,
  });
  const valueCurrency = spotOrderUtil.getByQtyType(qtyType, displayBaseCurrency, quoteCurrency, true);

  const createTimestamp = convertTimestampNs(createTimeNs);

  const iceberg = Number(displayQty) > 0 && Number(displayQty) < Number(qtyEv) && createTimestamp > IcebergOnlineTime;
  const icebergBaseQty = iceberg ? numUtil.floor(displayQty, baseQtyPrecision, baseQtyScale) : '0';
  const icebergQuoteQty = iceberg
    ? spotOrderUtil.calcValue({
        qtyType: 'ByBase',
        priceEp,
        baseQtyEv: displayQty,
        quoteQtyEv: 0,
        baseQtyScale,
        quoteQtyScale,
        quoteQtyPrecision,
        baseQtyPrecision,
      })
    : '0';

  return {
    orderID,
    symbol,
    displaySymbol,
    side,
    qtyType,
    qty,
    qtyEv,
    qtyWU: spotOrderUtil.joinWU(qty, qtyCurrency),
    qtyCurrency,
    supportMarginTrade: tradePair.supportMarginTrade,
    priceEp,
    price: numUtil.delimit(numUtil.floor(priceEp, pricePrecision, quoteQtyScale)),
    value,
    valueWU: spotOrderUtil.joinWU(value, valueCurrency),
    valueCurrency,
    filledQty: spotOrderUtil.calcQty(qtyType, baseFilledQty, quoteFilledQty),
    remainingQtyWU: spotOrderUtil.calcQtyWU(qtyType, baseRemainingQty, baseCurrency, quoteRemainingQty, quoteCurrency),
    remainingQty: spotOrderUtil.calcQty(qtyType, baseRemainingQty, quoteRemainingQty),
    ordType,
    ordStatus,
    createTimestamp,
    createTime: timeUtil.formatDateTime(createTimestamp),
    removable: spotOrderUtil.isFinalOrder(ordStatus),
    displayQty: iceberg ? displayQty : 0,
    icebergBaseQty,
    icebergQuoteQty,
  };
}

function convertToHistoryOrder<OrderRaw extends BaseOrderRaw>(
  raw: OrderRaw,
  tradePairs: TradePair[],
  mode: SpotTradeMode = 'Spot'
): HistoryOrder {
  const {
    orderID,
    symbol,
    side,
    qtyType,
    baseQtyEv,
    quoteQtyEv,
    ordType,
    ordStatus,
    priceEp,
    avgPriceEp,
    stopPxEp,
    createTimeNs,
  } = raw;
  const tradePair = tradePairs.find((o) => o.symbol === symbol);
  if (!tradePair) {
    return null;
  }
  const {displaySymbol, pricePrecision, displayBaseCurrency, quoteCurrency, baseQtyPrecision, quoteQtyPrecision} =
    tradePair;
  const {baseQtyScale, quoteQtyScale} = getQtyScale<TradePair>(mode, tradePair);

  const isLiqTrade = spotOrderUtil.isLiqTradeOrder(raw);
  const isMarket = spotOrderUtil.isMarketOrder(raw);
  const baseQty = numUtil.floor(baseQtyEv, baseQtyPrecision, baseQtyScale);
  const quoteQty = numUtil.floor(quoteQtyEv, quoteQtyPrecision, quoteQtyScale);
  const createTimestamp = convertTimestampNs(createTimeNs);
  const amount = spotOrderUtil.calcQty(qtyType, baseQty, quoteQty);
  const amountCurrency = spotOrderUtil.getByQtyType(qtyType, displayBaseCurrency, quoteCurrency);
  return {
    orderID,
    symbol,
    displaySymbol,
    side,
    amount,
    supportMarginTrade: tradePair.supportMarginTrade,
    amountWU: spotOrderUtil.joinWU(amount, amountCurrency),
    amountCurrency,
    price: convertPrice(isMarket || isLiqTrade, priceEp, pricePrecision, quoteQtyScale),
    fillPrice: calcFillPrice(avgPriceEp, pricePrecision, quoteQtyScale),
    value:
      isMarket || isLiqTrade
        ? '-'
        : spotOrderUtil.calcValueWU({
            qtyType,
            priceEp,
            baseQtyEv,
            quoteQtyEv,
            baseQtyScale,
            quoteQtyScale,
            baseQtyPrecision,
            quoteQtyPrecision,
            displayBaseCurrency,
            quoteCurrency,
          }),
    stopPx: calcStopPx(stopPxEp, ordType, pricePrecision, quoteQtyScale),
    ordType,
    ordStatus,
    createTimestamp,
    createTime: timeUtil.formatDateTime(createTimestamp),
  };
}

function convertToFillOrder<OrderRaw extends FillOrderRaw>(
  raw: OrderRaw,
  tradePairs: TradePair[],
  mode: SpotTradeMode = 'Spot'
): FillOrder {
  const {
    orderID,
    execID,
    symbol,
    side,
    qtyType,
    baseQtyEv,
    quoteQtyEv,
    execBaseQtyEv,
    execQuoteQtyEv,
    ordType,
    priceEp,
    execPriceEp,
    transactTimeNs,
  } = raw;
  const tradePair = tradePairs.find((o) => o.symbol === symbol);
  if (!tradePair) {
    return null;
  }
  const {displaySymbol, pricePrecision, displayBaseCurrency, quoteCurrency, baseQtyPrecision, quoteQtyPrecision} =
    tradePair;
  const {baseQtyScale, quoteQtyScale} = getQtyScale<TradePair>(mode, tradePair);

  const isLiqTrade = spotOrderUtil.isLiqTradeOrder(raw);
  const isMarket = spotOrderUtil.isMarketOrder(raw);
  const baseQty = numUtil.floor(baseQtyEv, baseQtyPrecision, baseQtyScale);
  const baseExecQty = numUtil.floor(execBaseQtyEv, baseQtyPrecision, baseQtyScale);
  const quoteQty = numUtil.floor(quoteQtyEv, quoteQtyPrecision, quoteQtyScale);
  const quoteExecQty = numUtil.floor(execQuoteQtyEv, quoteQtyPrecision, quoteQtyScale);

  const transactTimestamp = convertTimestampNs(transactTimeNs);
  return {
    orderID,
    execID,
    symbol,
    displaySymbol,
    side,
    supportMarginTrade: tradePair.supportMarginTrade,
    qty: spotOrderUtil.calcQtyWU(qtyType, baseQty, displayBaseCurrency, quoteQty, quoteCurrency),
    getQty: calcGetAmountWU(side, baseExecQty, displayBaseCurrency, quoteExecQty, quoteCurrency),
    spendQty: calcSpendAmountWU(side, baseExecQty, displayBaseCurrency, quoteExecQty, quoteCurrency),
    price: convertPrice(isMarket || isLiqTrade, priceEp, pricePrecision, quoteQtyScale),
    execPrice: numUtil.delimit(numUtil.floor(execPriceEp, pricePrecision, quoteQtyScale)),
    ordType,
    transactTimestamp,
    transactTime: timeUtil.formatDateTime(transactTimestamp),
    execBaseQtyEv,
    execQuoteQtyEv,
  };
}

function convertToConditionalOrder<OrderRaw extends OpenOrderRaw>(
  raw: OrderRaw,
  tradePairs: TradePair[],
  mode: SpotTradeMode = 'Spot'
): ConditionalOrder {
  const {
    orderID,
    symbol,
    side,
    qtyType,
    baseQtyEv,
    quoteQtyEv,
    leavesBaseQtyEv,
    leavesQuoteQtyEv,
    ordType,
    ordStatus,
    priceEp,
    stopPxEp,
    createTimeNs,
    stopDirection,
    displayQty,
  } = raw;
  const tradePair = tradePairs.find((o) => o.symbol === symbol);
  if (!tradePair) {
    return null;
  }
  const {
    displaySymbol,
    pricePrecision,
    baseCurrency,
    displayBaseCurrency,
    quoteCurrency,
    baseQtyPrecision,
    quoteQtyPrecision,
  } = tradePair;
  const {baseQtyScale, quoteQtyScale} = getQtyScale<TradePair>(mode, tradePair);

  const isLiqTrade = spotOrderUtil.isLiqTradeOrder(raw);
  const isMarket = spotOrderUtil.isMarketOrder(raw);
  const createTimestamp = convertTimestampNs(createTimeNs);
  const baseQty = numUtil.floor(baseQtyEv, baseQtyPrecision, baseQtyScale);
  const baseRemainingQty = numUtil.floor(leavesBaseQtyEv, baseQtyPrecision, baseQtyScale);
  const baseFilledQty = numUtil.floor(minus(baseQtyEv, leavesBaseQtyEv), baseQtyPrecision, baseQtyScale);
  const quoteQty = numUtil.floor(quoteQtyEv, quoteQtyPrecision, quoteQtyScale);
  const quoteRemainingQty = numUtil.floor(leavesQuoteQtyEv, quoteQtyPrecision, quoteQtyScale);
  const quoteFilledQty = numUtil.floor(minus(quoteQtyEv, leavesQuoteQtyEv), quoteQtyPrecision, quoteQtyScale);

  const qty = spotOrderUtil.calcQty(qtyType, baseQty, quoteQty);
  const qtyEv = Number(qtyType === 'ByBase' ? baseQtyEv : quoteQtyEv);

  const qtyCurrency = spotOrderUtil.getByQtyType(qtyType, displayBaseCurrency, quoteCurrency);
  const value = spotOrderUtil.calcValue({
    qtyType,
    priceEp,
    baseQtyEv,
    quoteQtyEv,
    baseQtyScale,
    quoteQtyScale,
    quoteQtyPrecision,
    baseQtyPrecision,
  });
  const valueCurrency = spotOrderUtil.getByQtyType(qtyType, displayBaseCurrency, quoteCurrency, true);

  const iceberg = Number(displayQty) > 0 && Number(displayQty) < Number(qtyEv) && createTimestamp > IcebergOnlineTime;
  const icebergBaseQty = iceberg ? numUtil.floor(displayQty, baseQtyPrecision, baseQtyScale) : '0';
  const icebergQuoteQty = iceberg
    ? spotOrderUtil.calcValue({
        qtyType: 'ByBase',
        priceEp,
        baseQtyEv: displayQty,
        quoteQtyEv: 0,
        baseQtyScale,
        quoteQtyScale,
        quoteQtyPrecision,
        baseQtyPrecision,
      })
    : '0';

  return {
    orderID,
    symbol,
    displaySymbol,
    side,
    isMarket,
    qtyType,
    qty,
    qtyWU: spotOrderUtil.joinWU(qty, qtyCurrency),
    qtyCurrency,
    supportMarginTrade: tradePair.supportMarginTrade,
    price: convertPrice(isMarket || isLiqTrade, priceEp, pricePrecision, quoteQtyScale),
    priceEp: Number(priceEp),
    qtyEv,
    value: isMarket || isLiqTrade ? '-' : value,
    valueWU: isMarket || isLiqTrade ? '-' : spotOrderUtil.joinWU(value, valueCurrency),
    valueCurrency,
    filledQty: spotOrderUtil.calcQty(qtyType, baseFilledQty, quoteFilledQty),
    remainingQtyWU: spotOrderUtil.calcQtyWU(qtyType, baseRemainingQty, baseCurrency, quoteRemainingQty, quoteCurrency),
    remainingQty: spotOrderUtil.calcQty(qtyType, baseRemainingQty, quoteRemainingQty),
    stopDirection: direction(stopDirection),
    stopPxEp,
    stopPx: numUtil.delimit(numUtil.floor(stopPxEp, pricePrecision, quoteQtyScale)),
    ordType,
    ordStatus,
    createTimestamp,
    createTime: timeUtil.formatDateTime(createTimestamp),
    createMDTime: timeUtil.formatMonthDayTime(createTimestamp),
    displayQty: iceberg ? displayQty : 0,
    icebergBaseQty,
    icebergQuoteQty,
  };
}

function direction(d) {
  if (d === 'Rising') {
    return '≥';
  }
  if (d === 'Falling') {
    return '≤';
  }
  return '';
}

function calcGetAmountWU(side, baseExecQty, baseCurrency, quoteExecQty, quoteCurrency) {
  if (side === 'Buy') {
    return numUtil.delimit(baseExecQty) + ' ' + baseCurrency;
  }
  if (side === 'Sell') {
    return numUtil.delimit(quoteExecQty) + ' ' + quoteCurrency;
  }
  return '';
}

function calcSpendAmountWU(side, baseExecQty, baseCurrency, quoteExecQty, quoteCurrency) {
  if (side === 'Buy') {
    return numUtil.delimit(quoteExecQty) + ' ' + quoteCurrency;
  }
  if (side === 'Sell') {
    return numUtil.delimit(baseExecQty) + ' ' + baseCurrency;
  }
  return '';
}

function calcFillPrice(avgPriceEp, precision, scale) {
  if (eq(avgPriceEp, 0)) {
    return '-';
  }
  return numUtil.delimit(numUtil.floor(avgPriceEp, precision, scale));
}

function calcStopPx(stopPxEp, ordType, precision, scale) {
  if (['Stop', 'StopLimit', 'LimitIfTouched', 'MarketIfTouched'].indexOf(ordType) < 0) {
    return '-';
  }
  return numUtil.delimit(numUtil.floor(stopPxEp, precision, scale));
}

interface QtyScale {
  baseQtyScale: number;
  quoteQtyScale: number;
}
function getQtyScale<T extends QtyScale>(mode: SpotTradeMode, {baseQtyScale, quoteQtyScale}: T): QtyScale {
  const isMargin = mode === 'Margin';
  if (isMargin) {
    return {baseQtyScale: 0, quoteQtyScale: 0};
  }
  return {baseQtyScale, quoteQtyScale};
}

function convertTimestampNs(timestampNs: number) {
  return Math.floor((timestampNs || 0) / 1e6);
}
